import { z } from 'zod'
import { ModelUtils } from './utils'
import type { Timestamp } from 'firebase/firestore'
import type { Timestamp as AdminTimestamp } from 'firebase-admin/firestore'

import type { QueryDocumentSnapshot, SnapshotOptions } from 'firebase/firestore'
import type { QueryDocumentSnapshot as AdminQueryDocumentSnapshot } from 'firebase-admin/firestore'

// Useful reference for paddle api
// https://developer.paddle.com/api-reference/subscriptions/overview

export class PaddleSubscriptionFactory {
  public PaddleSubscription
  public FirestorePaddleSubscription
  public PaddleItem
  public PaddleProduct
  public PaddleProductCustomData
  public PaddlePrice
  public PaddleCustomData
  public PaddleItemFirestore
  public paddleSubscriptionConverter
  public adminPaddleSubscriptionConverter
  private utils

  constructor(timestamp: typeof Timestamp | typeof AdminTimestamp) {
    this.utils = new ModelUtils(timestamp)

    this.PaddleCustomData = z.object({
      monocle_user_id: z.string(),
    })
    this.PaddlePrice = z.object({
      //paddle price ids begin with 'pri_'
      id: z.string().regex(/^pri_/),
      name: z.string(),
      status: z.enum(['active', 'inactive']),
      type: z.enum(['custom', 'standard']),
      unitPrice: z.object({
        amount: z.coerce.number().positive(),
        currencyCode: z.enum(['USD', 'EUR', 'GBP']),
      }),
    })

    this.PaddleProductCustomData = z.object({
      storage_quota_megabytes: z.coerce.number(),
    })

    this.PaddleProduct = z.object({
      //paddle product ids begin with 'pro_'
      id: z.string().regex(/^pro_/),
      name: z.string(),
      description: z.string(),
      type: z.enum(['custom', 'standard']),
      status: z.enum(['active', 'archived']),
      customData: this.PaddleProductCustomData.nullable(),
    })

    this.PaddleItem = z.object({
      status: z.enum(['active', 'inactive', 'trialing']),
      price: this.PaddlePrice,
      product: this.PaddleProduct,
      recurring: z.boolean(),
      createdAt: this.utils.date,
      updatedAt: this.utils.date,
      nextBilledAt: this.utils.date.nullable(),
      previouslyBilledAt: this.utils.date.nullable(),
    })

    this.PaddleItemFirestore = this.PaddleItem.extend({
      createdAt: this.utils.dateForFirestore,
      updatedAt: this.utils.dateForFirestore,
      nextBilledAt: this.utils.dateForFirestore.nullable(),
      previouslyBilledAt: this.utils.dateForFirestore.nullable(),
    })

    this.PaddleSubscription = z.object({
      //paddle subscription ids begin with 'sub_'
      id: z.string().regex(/^sub_/),
      createdAt: this.utils.date,
      startedAt: this.utils.date,
      updatedAt: this.utils.date,
      nextBilledAt: this.utils.date.nullable(),
      canceledAt: this.utils.date.nullable(),
      status: z.enum(['active', 'canceled', 'paused', 'past_due', 'trialing']),
      currentBillingPeriod: z
        .object({
          startsAt: this.utils.date,
          endsAt: this.utils.date,
        })
        .nullable(),
      //paddle customer ids begin with 'ctm_'
      customerId: z.string().regex(/^ctm_/),
      items: this.PaddleItem.array(),
      customData: this.PaddleCustomData,
    })
    this.FirestorePaddleSubscription = this.PaddleSubscription.extend({
      createdAt: this.utils.dateForFirestore,
      startedAt: this.utils.dateForFirestore,
      updatedAt: this.utils.dateForFirestore,
      nextBilledAt: this.utils.dateForFirestore.nullable(),
      canceledAt: this.utils.dateForFirestore.nullable(),
      currentBillingPeriod: z
        .object({
          startsAt: this.utils.dateForFirestore,
          endsAt: this.utils.dateForFirestore,
        })
        .nullable(),
      items: this.PaddleItemFirestore.array(),
      customData: this.PaddleCustomData,
    })

    type PaddleSubscription = z.infer<typeof this.PaddleSubscription>
    type FirestorePaddleSubscription = z.infer<
      typeof this.FirestorePaddleSubscription
    >

    this.paddleSubscriptionConverter = {
      toFirestore: (
        bundle: PaddleSubscription,
      ): FirestorePaddleSubscription => {
        return this.FirestorePaddleSubscription.parse(bundle)
      },
      fromFirestore: (
        snapshot: QueryDocumentSnapshot<
          PaddleSubscription,
          FirestorePaddleSubscription
        >,
        options: SnapshotOptions,
      ): PaddleSubscription => {
        const data = snapshot.data(options)
        const returnDat = this.PaddleSubscription.parse({
          ...data,
          id: snapshot.id,
        })
        return returnDat
      },
    }

    this.adminPaddleSubscriptionConverter = {
      toFirestore: (subscription: PaddleSubscription) => {
        return this.FirestorePaddleSubscription.parse(subscription)
      },

      fromFirestore: (
        snapshot: AdminQueryDocumentSnapshot<PaddleSubscription>,
      ): PaddleSubscription => {
        const data = snapshot.data()!
        const returnDat = this.PaddleSubscription.parse({
          ...data,
          id: snapshot.id,
        })
        return returnDat as PaddleSubscription
      },
    }
  }

  static storageQuota(
    subscriptions:
      | z.infer<typeof PaddleSubscriptionFactory.prototype.PaddleSubscription>[]
      | undefined,
  ) {
    if (subscriptions == undefined || subscriptions.length === 0) {
      // If there are no subscriptions, return a default value
      return NO_SUBSCRIPTION_DEFAULT_STORAGE_QUOTA * 1000000
    } else {
      let storageQuotaMegabytes = 0

      for (const subscription of subscriptions) {
        for (const item of subscription.items) {
          if (item.product.customData?.storage_quota_megabytes) {
            storageQuotaMegabytes +=
              item.product.customData.storage_quota_megabytes
          }
        }
      }
      return storageQuotaMegabytes * 1000000
    }
  }
  static subscriptionStartDate(
    subscriptions:
      | z.infer<typeof PaddleSubscriptionFactory.prototype.PaddleSubscription>[]
      | undefined,
  ) {
    if (subscriptions == undefined || subscriptions.length === 0) {
      return null
    } else {
      // Find the earliest startedAt date from all subscriptions
      const startDates = subscriptions.map((sub) => sub.startedAt)
      const earliestStartDate = new Date(
        Math.min(...startDates.map((d) => d.getTime())),
      )
      return earliestStartDate
    }
  }

  static subscriptionEndDate(
    subscriptions:
      | z.infer<typeof PaddleSubscriptionFactory.prototype.PaddleSubscription>[]
      | undefined,
  ) {
    if (subscriptions == undefined || subscriptions.length === 0) {
      return null
    } else {
      // Find the latest nextBilledAt date from all subscriptions
      const endDates = subscriptions.map((sub) => sub.nextBilledAt)
      const latestEndDate = new Date(
        Math.max(...endDates.map((d) => d?.getTime() ?? 0)),
      )
      return latestEndDate
    }
  }
}
const NO_SUBSCRIPTION_DEFAULT_STORAGE_QUOTA = 2000 // 2000MB
